import { Injectable } from "@angular/core";
import { combineLatest, Observable, of } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import {
  CfsTemplateError,
  CfsTemplateErrorTypes,
  ContentSettings,
  isImportedVideoTemplate,
  isVimeoTemplate,
  isYoutubeTemplate,
  Template,
  TemplateBasePreviewOptions
} from "clearline-common";
import { DigitalAssetGetDto, DigitalAssetService, TemplateContentType, TemplateService } from "clearline-api";
import { UserAgentService } from "core-kit";

interface BasePreviewOptionsAsset {
  [key: string]: TemplateBasePreviewOptions;
}

interface TemplateAssetIdLists {
  assetIds: number[];
}

@Injectable({
  providedIn: "root"
})
export class TemplatesService {
  constructor(
    private templateService: TemplateService,
    private digitalAssetService: DigitalAssetService,
    private userAgentService: UserAgentService
  ) {}

  getPreparedTemplates$(templateList: Template[]): Observable<Template[]> {
    // preloading content preview options (for lottie templates):
    const lottieTemplates: Template[] = templateList.filter((item: Template) => TemplatesService.getLottieTemplateFilterCondition(item));
    const { assetIds } = this.getUniqueContentIdLists(lottieTemplates);

    if (assetIds?.length) {
      const assetOptions$ = assetIds?.length
        ? combineLatest(assetIds.map((item: number) => this.getTemplateBasePreviewOptions$(item)))
        : of([]);

      return assetOptions$.pipe(
        map((assetOptions) => {
          return this.getHandledTemplatesResult(templateList, lottieTemplates, assetOptions as TemplateBasePreviewOptions[]);
        })
      );
    }

    return of(templateList);
  }

  private getUniqueContentIdLists(lottieTemplates: Template[]): TemplateAssetIdLists {
    const assetIds: number[] = [];

    lottieTemplates.forEach((item: Template) => {
      const assetId: number | undefined = TemplatesService.getAssetId(item);

      if (assetId && !assetIds.includes(assetId)) assetIds.push(assetId);
    });

    return { assetIds };
  }

  private getTemplateBasePreviewOptions$(assetId: number): Observable<TemplateBasePreviewOptions | null> {
    return this.digitalAssetService.getAsset(assetId).pipe(
      catchError(() => of(null as unknown as DigitalAssetGetDto)),
      mergeMap((asset: DigitalAssetGetDto) => {
        const contentUrl = (asset as DigitalAssetGetDto)?.contentUrl;

        return combineLatest([of(asset), this.getContentByUrl$(contentUrl)]);
      }),
      map(([digitalAsset, json]: [DigitalAssetGetDto, string]) => {
        return digitalAsset ? { digitalAsset, json } : null;
      })
    );
  }

  private getContentByUrl$(contentUrl: string = ""): Observable<string> {
    if (contentUrl) {
      return this.templateService.getContentByUrl(contentUrl as string).pipe(catchError(() => of("")));
    }

    return of("");
  }

  private getHandledTemplatesResult(
    templateList: Template[],
    lottieTemplates: Template[],
    options: TemplateBasePreviewOptions[] = []
  ): Template[] {
    const assets: BasePreviewOptionsAsset = TemplatesService.getBasePreviewOptionsAsset(options);

    if (TemplatesService.isObjectEmpty(assets)) {
      return templateList.filter((item: Template) => !lottieTemplates.some((lottieItem: Template) => item.id === lottieItem.id));
    }

    lottieTemplates.forEach((item: Template) => {
      const contentSettings: ContentSettings = item.templateProperties as ContentSettings;
      const itemAssetId: number | undefined = contentSettings?.assetId;
      const templateContentType: TemplateContentType = item.templateContentType as TemplateContentType;

      if (itemAssetId && assets[itemAssetId]) {
        const itemOptions: TemplateBasePreviewOptions = assets[itemAssetId];
        item.previewOptions = { ...itemOptions, contentSettings, templateContentType };
      }
    });

    return templateList;
  }

  private static getBasePreviewOptionsAsset(list: TemplateBasePreviewOptions[]): BasePreviewOptionsAsset {
    return list?.reduce((acc: BasePreviewOptionsAsset, item: TemplateBasePreviewOptions) => {
      const id: number = this.getBaseContentIdByOptions(item);

      if (id >= 0 && !acc.hasOwnProperty(id)) {
        acc[id] = item;
      }

      return acc;
    }, {} as BasePreviewOptionsAsset);
  }

  private static getBaseContentIdByOptions(options: TemplateBasePreviewOptions): number {
    const { digitalAsset } = options;

    return digitalAsset?.id as number;
  }

  private static isObjectEmpty(object: Object) {
    return Object.keys(object).length === 0;
  }

  private static getLottieTemplateFilterCondition(template: Template): boolean {
    const assetId: number | undefined = TemplatesService.getAssetId(template);

    return !!assetId;
  }

  private static getAssetId(template: Template): number | undefined {
    return (template.templateProperties as ContentSettings)?.assetId;
  }

  getTemplateFilterError(template: Template): CfsTemplateError | null {
    if (isImportedVideoTemplate(template) && !this.isImportedVideoTemplatesAccessible()) {
      return {
        type: CfsTemplateErrorTypes.BrowserCompatibility,
        message: "Imported video templates are not supported in this browser version",
        data: { field: "templateId", value: template.id?.toString() }
      };
    } else if (isVimeoTemplate(template) && !this.isVimeoTemplateAccessible()) {
      return {
        type: CfsTemplateErrorTypes.FeatureTemporarilyDisabled,
        message: "Vimeo templates are temporarily not supported",
        data: { field: "templateId", value: template.id?.toString() }
      };
    } else if (isYoutubeTemplate(template) && !this.isYouTubeTemplateAccessible()) {
      return {
        type: CfsTemplateErrorTypes.BrowserCompatibility,
        message: "Youtube templates are not supported in this browser version",
        data: { field: "templateId", value: template.id?.toString() }
      };
    }

    return null;
  }

  // Template access checks:

  private isVimeoTemplateAccessible(): boolean {
    return false; // temporary disable, fixme
  }

  private isImportedVideoTemplatesAccessible(): boolean {
    return !this.userAgentService.isCloverDevices();
  }

  private isYouTubeTemplateAccessible(): boolean {
    return !this.userAgentService.isProblematicBrowserForYouTube();
  }
}
